'use client';

import type { CartShipment } from '@eliadmin/core/src/api/cart/cart-types';
import { ShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import type { ShipmentProviderBody } from '@eliadmin/core/src/components/cart/detail/shipment-types/cart-shipment-provider';
import { ErrorBoundary } from '@eliadmin/core/src/components/error/error-boundary';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { lazy, Suspense, type ComponentProps } from 'react';

// Inherited core 3.0.121 implementations load only when their dialog is rendered.
const LazyCartSPSDialog = lazy(() =>
  import('@eliadmin/core/src/components/cart/detail/shipment-types/cart-sps-dialog').then(
    module => ({ default: module.CartSPSDialog }),
  ),
);
const LazyCartGLSDialog = lazy(() =>
  import('@eliadmin/core/src/components/cart/detail/shipment-types/cart-gls-dialog').then(
    module => ({ default: module.CartGLSDialog }),
  ),
);

// The dialog store freezes React nodes with Immer. Keep lazy's mutable payload
// behind a function component so it is created outside the stored element tree.
const CartSPSDialog = Object.assign(
  (props: ComponentProps<typeof LazyCartSPSDialog>) => <LazyCartSPSDialog {...props} />,
  { id: 'CART_SPS_DIALOG' },
);
const CartGLSDialog = Object.assign(
  (props: ComponentProps<typeof LazyCartGLSDialog>) => <LazyCartGLSDialog {...props} />,
  { id: 'CART_GLS_DIALOG' },
);

declare global {
  interface Window {
    getPacketaWidget: (cb: (p: any) => void) => void;
  }
}

interface CartScriptsProps {
  shipment: CartShipment;
  callback: (p: any) => void;
}

function ShipmentDialogLoading({
  dialogId,
  failed = false,
}: {
  dialogId: string;
  failed?: boolean;
}) {
  const close = useResponsiveDialogStore(s => s.close);
  const { t } = useTranslations();

  return (
    <ResponsiveDialogContent
      className="flex flex-col gap-4 p-6"
      dialogProps={{ 'aria-describedby': undefined }}
      drawerProps={{ 'aria-describedby': undefined }}
    >
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle>{t('text440')}</ResponsiveDialogTitle>
      </ResponsiveDialogHeader>
      {failed ? (
        <p role="alert">{t('text530')}</p>
      ) : (
        <div
          role="status"
          aria-label={t('text440')}
          className="flex min-h-32 items-center justify-center"
        >
          <span aria-hidden="true">
            <Spinner className="h-6 w-6" />
          </span>
        </div>
      )}
      <Button type="button" variant="outline" onClick={() => close(dialogId)}>
        {t('text377')}
      </Button>
    </ResponsiveDialogContent>
  );
}

export function useCartShipmentProvider(props: CartScriptsProps) {
  const openDialog = useResponsiveDialogStore(s => s.open);

  switch (props?.shipment?.type) {
    case ShipmentType.PACKETA:
      return {
        trigger: () => {
          window.getPacketaWidget(props.callback);
        },
      };
    case ShipmentType.SPS_ZBOX: {
      return {
        trigger: () => {
          const dialogId = `${CartSPSDialog.id}-${props.shipment.id}`;
          openDialog({
            // TODO: add initial place
            content: (
              <ErrorBoundary
                errorComponent={() => <ShipmentDialogLoading dialogId={dialogId} failed />}
              >
                <Suspense fallback={<ShipmentDialogLoading dialogId={dialogId} />}>
                  <CartSPSDialog
                    onPlaceSelected={props.callback}
                    dialogId={dialogId}
                    initialPlace={
                      props.shipment.settings &&
                      'branch_id' in props.shipment.settings &&
                      props.shipment.settings.branch_id
                        ? (props.shipment.settings as ShipmentProviderBody)
                        : undefined
                    }
                  />
                </Suspense>
              </ErrorBoundary>
            ),
            id: dialogId,
          });
        },
      };
    }
    case ShipmentType.GLS_ZBOX: {
      return {
        trigger: () => {
          const dialogId = `${CartGLSDialog.id}-${props.shipment.id}`;
          openDialog({
            content: (
              <ErrorBoundary
                errorComponent={() => <ShipmentDialogLoading dialogId={dialogId} failed />}
              >
                <Suspense fallback={<ShipmentDialogLoading dialogId={dialogId} />}>
                  <CartGLSDialog onPlaceSelected={props.callback} dialogId={dialogId} />
                </Suspense>
              </ErrorBoundary>
            ),
            id: dialogId,
          });
        },
      };
    }
    default:
      return null;
  }
}
